import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import {
  tutorService,
  TutorMessage as TutorMessageType,
  TutorConversation,
  QuickHelpType,
} from '../../services/tutorService';
import TutorMessage from './TutorMessage';
import TutorInput from './TutorInput';
import QuickHelpButtons from './QuickHelpButtons';

interface TutorChatProps {
  courseId?: number;
  lessonId?: number;
  lessonTitle?: string;
}

// Issue #197 (EU-KI-Act Art. 50): Erst-Aufruf-Disclaimer — einmalig pro Browser.
const AI_DISCLAIMER_SEEN_KEY = 'oa_ai_tutor_disclaimer_seen';

const TutorChat: React.FC<TutorChatProps> = ({ courseId, lessonId, lessonTitle }) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<TutorMessageType[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversations, setConversations] = useState<TutorConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(
    () => localStorage.getItem(AI_DISCLAIMER_SEEN_KEY) !== '1'
  );

  const handleDisclaimerAccept = () => {
    localStorage.setItem(AI_DISCLAIMER_SEEN_KEY, '1');
    setShowDisclaimer(false);
  };

  useEffect(() => {
    loadConversations();
  }, [courseId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      const data = await tutorService.getConversations(courseId);
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (message: string) => {
    // Add user message immediately
    const userMessage: TutorMessageType = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setError(null);

    try {
      const response = await tutorService.askQuestion(message, {
        courseId,
        lessonId,
        conversationId: conversationId || undefined,
      });

      setConversationId(response.conversationId);
      setMessages(response.messages);
      loadConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler bei der Anfrage');
      // Remove the user message on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickHelpResponse = (response: string, type: QuickHelpType) => {
    // Create a synthetic conversation with the quick help response
    const typeLabels: Record<QuickHelpType, string> = {
      explain: 'Einfachere Erklärung',
      example: 'Beispiel',
      quiz: 'Verständnisfrage',
      summary: 'Zusammenfassung',
    };

    const assistantMessage: TutorMessageType = {
      role: 'assistant',
      content: `**${typeLabels[type]}:**\n\n${response}`,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, assistantMessage]);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
  };

  const handleLoadConversation = async (id: number) => {
    try {
      const conversation = await tutorService.getConversation(id);
      setMessages(conversation.messages);
      setConversationId(id);
      setShowHistory(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    }
  };

  const handleDeleteConversation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await tutorService.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (conversationId === id) {
        handleNewConversation();
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Issue #197 (EU-KI-Act Art. 50): Disclaimer beim ersten Aufruf */}
      <Dialog open={showDisclaimer} onClose={handleDisclaimerAccept} data-testid="ai-disclaimer-dialog">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BotIcon color="primary" />
          {t('tutor.disclaimerTitle', 'Du chattest mit einer KI')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('tutor.disclaimerBody', 'Der Lernassistent ist ein KI-System. Antworten können ungenau oder unvollständig sein — verlasse dich bei wichtigen Fragen nicht allein darauf und frage im Zweifel deine Trainer:in. Deine Konversationen sind privat, werden auf unseren eigenen Servern verarbeitet und du kannst sie jederzeit löschen. Bitte gib keine sensiblen persönlichen Informationen ein.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisclaimerAccept} variant="contained" data-testid="ai-disclaimer-accept">
            {t('tutor.disclaimerAccept', 'Verstanden')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon color="primary" />
            <Typography variant="h6">{t('tutor.title', 'KI-Lernassistent')}</Typography>
            {/* Issue #197: persistente KI-Kennzeichnung (Art. 50 EU-KI-Act) */}
            <Chip
              size="small"
              label={t('tutor.aiLabel', 'KI')}
              color="primary"
              variant="outlined"
              data-testid="ai-label-chip"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('tutor.newConversation', 'Neues Gespräch')}>
              <IconButton size="small" onClick={handleNewConversation}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('tutor.history', 'Verlauf')}>
              <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Conversation History */}
        <Collapse in={showHistory}>
          <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
            {conversations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('tutor.noConversations', 'Keine früheren Gespräche')}
              </Typography>
            ) : (
              <List dense>
                {conversations.map((conv) => (
                  <ListItemButton
                    key={conv.id}
                    selected={conv.id === conversationId}
                    onClick={() => handleLoadConversation(conv.id)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={conv.title}
                      secondary={new Date(conv.updatedAt).toLocaleDateString('de-DE')}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                    <ListItemSecondaryAction>
                      <Chip label={conv.messageCount} size="small" sx={{ mr: 1 }} />
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleDeleteConversation(conv.id, e)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Quick Help Buttons */}
      {lessonId && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('tutor.quickHelpLabel', 'Schnelle Hilfe')}:
          </Typography>
          <QuickHelpButtons
            lessonId={lessonId}
            onResponse={handleQuickHelpResponse}
            disabled={loading}
          />
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <BotIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('tutor.welcomeTitle', 'Hallo! Ich bin dein Lernassistent.')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lessonTitle
                ? t('tutor.welcomeWithLesson', 'Stelle mir Fragen zur Lektion "%{lesson}"', {
                    lesson: lessonTitle,
                  })
                : t('tutor.welcomeGeneral', 'Stelle mir eine Frage zum Kursinhalt.')}
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg, index) => (
              <TutorMessage key={index} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Input */}
      <TutorInput
        onSend={handleSend}
        loading={loading}
        placeholder={
          lessonTitle
            ? t('tutor.inputWithLesson', 'Frage zu "%{lesson}"...', { lesson: lessonTitle })
            : undefined
        }
      />
    </Box>
  );
};

export default TutorChat;
